/**
 * The read outcome a monitoring page renders from, derived from the fields a
 * TanStack query result already carries. Same four outcomes, derived the same
 * way, as the dashboards (CollateralMonitoring.tsx, ComplianceStatus.tsx,
 * RiskDashboard.tsx): QueryReadOutcome in
 * schema-registry/admin-portal-ui.openapi.yaml.
 *
 *   loading -> a first fetch in flight
 *   failed  -> a first fetch that rejected; nothing was read
 *   unread  -> a first fetch that has not run (TanStack v5 reports a PAUSED
 *              first fetch as pending with isFetching false)
 *   read    -> a payload in hand, kept through a rejected background refetch
 *
 * A figure, a count or a measured-empty sentence is printable only from `read`.
 * Before this, Asset Classification, Valuation History and Concentration Risk
 * printed `?? 0` totals and "No positions on record" under their own error
 * banner, so a failed read looked like an empty book.
 *
 * The two tempo policy editors (tempo/CoveragePolicyPage.tsx and
 * tempo/PoolAppetitePage.tsx, GH #2926) gate their no-version banner and their
 * editing surface on `read` through the same helpers.
 */

import type { components as AdminPortalUiComponents } from '@aaimplatform/institutional-types/generated/admin-portal-ui';

export type ReadOutcome = AdminPortalUiComponents['schemas']['QueryReadOutcome'];

export function readOutcome(query: {
  isError: boolean;
  isLoading: boolean;
  data: unknown;
}): ReadOutcome {
  if (query.isError && query.data === undefined) return 'failed';
  if (query.isLoading) return 'loading';
  if (query.data !== undefined) return 'read';
  return 'unread';
}

/** A read whose background refetch rejected after a successful read. */
export function refreshFailed(query: { isError: boolean; data: unknown }): boolean {
  return query.isError && query.data !== undefined;
}

/** The local clock time of a read, for a refresh-failed notice. */
export function readTime(dataUpdatedAt: number): string {
  return new Date(dataUpdatedAt).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}
